import { RawHubspotHeaders } from '../types/index.js';
import { API_BASE_URL } from '../constants/api';

// Backend API configuration for HubSpot proxy
const BACKEND_API_BASE = API_BASE_URL;

/**
 * Fetches all deals from HubSpot API via backend proxy
 * @param {string} token - HubSpot OAuth token
 * @param {object} options - Optional parameters
 * @returns {Promise<Array>} - Array of raw HubSpot deal records
 */
export async function fetchHubspotDeals(token, options = {}) {
  if (!token) {
    throw new Error('HubSpot API token is required');
  }

  try {
    const response = await fetch(`${BACKEND_API_BASE}/hubspot/deals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        options
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HubSpot API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch HubSpot deals');
    }

    console.log(`Fetched ${data.deals.length} deals from HubSpot via backend`);
    
    // Return the full result object including save results
    return {
      deals: data.deals,
      saveResults: data.saveResults,
      totalCount: data.totalCount,
      importBatch: data.importBatch
    };
  } catch (error) {
    console.error('Error fetching HubSpot deals:', error);
    throw error;
  }
}

/**
 * Validates HubSpot API token by making a test call
 * @param {string} token - HubSpot OAuth token
 * @returns {Promise<boolean>} - True if token is valid
 */
export async function validateHubspotToken(token) {
  if (!token) {
    return false;
  }

  try {
    const response = await fetch(`${BACKEND_API_BASE}/hubspot/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token })
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.success || false;
  } catch (error) {
    console.error('Error validating HubSpot token:', error);
    return false;
  }
}

/**
 * Tests HubSpot connection (alias for validateHubspotToken)
 * @param {string} token - HubSpot OAuth token
 * @returns {Promise<Object>} - Result object with success status and message
 */
export async function testHubspotConnection(token) {
  try {
    const isValid = await validateHubspotToken(token);
    return {
      success: isValid,
      message: isValid ? 'Connection successful!' : 'Invalid API token or connection failed'
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Connection test failed'
    };
  }
}

/**
 * Transforms raw HubSpot deal data to standardized format
 * @param {Array} rawDeals - Raw deals from HubSpot API
 * @returns {Array} - Array of formatted deal records
 */
export function transformHubspotData(rawDeals) {
  if (!Array.isArray(rawDeals)) {
    throw new Error('Invalid input: expected array of deals');
  }

  return rawDeals.map((deal, index) => {
    try {
      const properties = deal.properties || {};
      
      return {
        [RawHubspotHeaders.RECORD_ID]: properties.hs_object_id || '',
        [RawHubspotHeaders.DEAL_NAME]: properties.dealname || '',
        [RawHubspotHeaders.RESPONSIBLE_PARTY_FIRST_NAME]: properties.custom_responsible_party_first_name || '',
        [RawHubspotHeaders.RESPONSIBLE_PARTY_LAST_NAME]: properties.custom_responsible_party_last_name || '',
        [RawHubspotHeaders.CLIENT_NAME]: properties.custom_client_name || '',
        [RawHubspotHeaders.PHONE_NUMBER_CLIENT]: properties.custom_phone_number_client || '',
        [RawHubspotHeaders.PHONE_NUMBER_RESPONSIBLE_PARTY]: properties.custom_phone_number_responsible_party || '',
        [RawHubspotHeaders.EMAIL_RESPONSIBLE_PARTY]: properties.custom_email_responsible_party || '',
        [RawHubspotHeaders.EMAIL_CLIENT]: properties.custom_email_client || '',
        [RawHubspotHeaders.DEAL_STAGE]: properties.dealstage || '',
        [RawHubspotHeaders.CLOSE_DATE]: properties.closedate || '',
        [RawHubspotHeaders.PIPELINE]: properties.pipeline || '',
        [RawHubspotHeaders.ASSESSMENT_STREET_ADDRESS]: properties.custom_assessment_street_address || '',
        [RawHubspotHeaders.ASSESSMENT_CITY]: properties.custom_assessment_city || '',
        [RawHubspotHeaders.ASSESSMENT_POSTAL_CODE]: properties.custom_assessment_postal_code || '',
        [RawHubspotHeaders.ASSESSMENT_STATE]: properties.custom_assessment_state || '',
        [RawHubspotHeaders.SERVICE_CITY]: properties.custom_service_city || '',
        [RawHubspotHeaders.SERVICE_POSTAL_CODE]: properties.custom_service_postal_code || '',
        [RawHubspotHeaders.SERVICE_STATE]: properties.custom_service_state || '',
        [RawHubspotHeaders.SERVICE_STREET_ADDRESS]: properties.custom_service_street_address || '',
        [RawHubspotHeaders.WELLSKY_DATABASE_ID]: properties.custom_wellsky_database_id || '',
        [RawHubspotHeaders.INSIDE_SALES_REP]: properties.custom_inside_sales_rep || '',
        [RawHubspotHeaders.COMMISSION_ELIGIBLE]: properties.custom_commission_eligible || false,
        [RawHubspotHeaders.CLIENT_TYPE]: properties.custom_client_type || ''
      };
    } catch (error) {
      console.warn(`Error transforming deal at index ${index}:`, error);
      return null;
    }
  }).filter(deal => deal !== null);
}

/**
 * Generates statistics for HubSpot data
 * @param {Array} deals - Array of HubSpot deals
 * @returns {Object} - Statistics object
 */
export function getHubspotStats(deals) {
  if (!Array.isArray(deals)) {
    return {
      totalRecords: 0,
      commissionEligible: 0,
      dealStages: {},
      pipelines: {},
      salesReps: {}
    };
  }

  const stats = {
    totalRecords: deals.length,
    commissionEligible: 0,
    dealStages: {},
    pipelines: {},
    salesReps: {}
  };

  deals.forEach(deal => {
    // Count commission eligible
    if (deal[RawHubspotHeaders.COMMISSION_ELIGIBLE]) {
      stats.commissionEligible++;
    }

    // Count deal stages
    const stage = deal[RawHubspotHeaders.DEAL_STAGE] || 'Unknown';
    stats.dealStages[stage] = (stats.dealStages[stage] || 0) + 1;

    // Count pipelines
    const pipeline = deal[RawHubspotHeaders.PIPELINE] || 'Unknown';
    stats.pipelines[pipeline] = (stats.pipelines[pipeline] || 0) + 1;

    // Count sales reps
    const rep = deal[RawHubspotHeaders.INSIDE_SALES_REP] || 'Unknown';
    stats.salesReps[rep] = (stats.salesReps[rep] || 0) + 1;
  });

  return stats;
}

/**
 * Retrieves saved HubSpot deals from the database
 * @param {object} params - Query parameters for filtering and pagination
 * @returns {Promise<object>} - Object containing deals array and pagination info
 */
export async function getSavedHubspotDeals(params = {}) {
  console.log('🔍 getSavedHubspotDeals called with params:', params);
  
  try {
    const queryParams = new URLSearchParams();
    
    // Add all parameters to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const url = `${BACKEND_API_BASE}/hubspot/deals?${queryParams}`;
    console.log('📡 HubSpot Service: Fetching from URL:', url);
    console.log('📡 HubSpot Service: BACKEND_API_BASE:', BACKEND_API_BASE);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    console.log('📨 HubSpot Service: Response status:', response.status, response.statusText);
    console.log('📨 HubSpot Service: Response ok:', response.ok);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ HubSpot Service: Response not ok:', response.status, errorData);
      throw new Error(errorData.message || `Error fetching saved deals: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('📊 HubSpot Service: Response data:', data);
    
    if (!data.success) {
      console.error('❌ HubSpot Service: Data success false:', data.message);
      throw new Error(data.message || 'Failed to fetch saved HubSpot deals');
    }

    console.log(`✅ HubSpot Service: Retrieved ${data.deals?.length || 0} saved deals from database`);
    
    const result = {
      success: true,
      deals: data.deals || [],
      pagination: data.pagination,
      totalCount: data.pagination?.total || data.deals?.length || 0
    };
    
    console.log('🎯 HubSpot Service: Returning result:', result);
    return result;
  } catch (error) {
    console.error('💥 HubSpot Service Error retrieving saved deals:', error);
    throw error;
  }
}
